import { useState } from "react";

import * as service from "../services/creatorVideosService";

import { validateVideoPriceAmount } from "../forms/uploads/utils/helper/videoPricing";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useCreatorVideos = (username) => {
  const [videoThumbs, setVideoThumbs] = useState([]);
  const [video, setVideo] = useState({});
  const [videoData, setVideoData] = useState({});

  const [searchVideoTagResults, setSearchVideoTagResults] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getCreatorVideoThumbs = async (limit, offset) => {
    if (!username) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getCreatorVideoThumbs(username, limit, offset);

      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.videos)
          ? res.data.videos
          : [];

      setVideoThumbs((current) =>
        Number(offset) > 0
          ? [
              ...current.filter(
                (item) =>
                  !data.some(
                    (incoming) =>
                      String(incoming.videoTag) === String(item.videoTag),
                  ),
              ),
              ...data,
            ]
          : data,
      );

      return data;
    } catch (requestError) {
      console.error("getCreatorVideoThumbs failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to load videos"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getCreatorVideoData = async (videoTag) => {
    if (!username || !videoTag) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getCreatorVideoData(username, videoTag);

      const data = res?.data?.data ?? res?.data ?? {};

      setVideoData(data);

      return data;
    } catch (requestError) {
      console.error("getCreatorVideoData failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to load video details"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getCreatorVideoEditData = async (videoTag) => {
    if (!username || !videoTag) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getCreatorVideoEditData(username, videoTag);

      const result = res?.data?.data ?? res?.data ?? {};

      const currentVideo = result?.video ?? result;

      if (
        !currentVideo ||
        typeof currentVideo !== "object" ||
        !(currentVideo.videoTag ?? currentVideo.video_tag)
      ) {
        throw new Error("Video edit data was not found.");
      }

      const rawDuration =
        currentVideo.videoDuration ??
        currentVideo.video_duration ??
        currentVideo.duration;

      const rawPrice = currentVideo.priceAmount ?? currentVideo.price_amount;

      const parsedDuration = Number(rawDuration);

      const normalizedVideo = {
        ...currentVideo,

        videoTag: currentVideo.videoTag ?? currentVideo.video_tag,

        videoDuration:
          rawDuration !== undefined &&
          rawDuration !== null &&
          rawDuration !== "" &&
          Number.isFinite(parsedDuration)
            ? parsedDuration
            : null,

        priceAmount:
          rawPrice === undefined ||
          rawPrice === null ||
          String(rawPrice).trim() === ""
            ? ""
            : String(rawPrice),
      };

      setVideoData(normalizedVideo);

      return {
        video: normalizedVideo,
      };
    } catch (requestError) {
      console.error("getCreatorVideoEditData failed:", requestError);

      setVideoData({});

      setError(getErrorMessage(requestError, "Failed to load video edit data"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateCreatorVideo = async (videoTag, data) => {
    if (!username || !videoTag || !data) {
      setError("Creator username, video tag and data are required.");
      return false;
    }

    const storedVideoTag = String(
      videoData?.videoTag ?? videoData?.video_tag ?? "",
    );

    if (storedVideoTag !== String(videoTag)) {
      setError("Load the video edit data before updating.");
      return false;
    }

    const videoDuration = Number(videoData?.videoDuration);

    const pricing = validateVideoPriceAmount(data.priceAmount, videoDuration);

    if (!pricing.valid) {
      setError(pricing.message);
      return false;
    }

    const payload = {
      title: String(data.title || "").trim(),
      sport: String(data.sport || "").trim(),
      description: String(data.description || "").trim(),
      visibility: String(data.visibility || "private")
        .trim()
        .toLowerCase(),
      priceAmount: pricing.priceAmount,
    };

    if (!payload.title || !payload.sport) {
      setError("Title and sport are required.");
      return false;
    }

    if (!["public", "unlisted", "private"].includes(payload.visibility)) {
      setError("Select a valid video visibility.");
      return false;
    }

    try {
      setLoading(true);
      setError("");

      const response = await service.updateCreatorVideo(
        username,
        videoTag,
        payload,
      );

      const result = response?.data;

      if (!result?.success) {
        setError(result?.message || "Failed to update video details.");
        return false;
      }

      setVideoData((previous) => ({
        ...previous,
        ...payload,
      }));

      setVideoThumbs((previous) =>
        Array.isArray(previous)
          ? previous.map((item) =>
              String(item?.videoTag ?? item?.video_tag) === String(videoTag)
                ? { ...item, ...payload }
                : item,
            )
          : previous,
      );

      return result;
    } catch (requestError) {
      const status = requestError?.response?.status;
      const responseData = requestError?.response?.data;

      console.error("updateCreatorVideo failed:", {
        status,
        response: responseData,
        payload,
      });

      setError(
        responseData?.message ||
          getErrorMessage(requestError, "Failed to update video details."),
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteCreatorVideo = async (videoTag) => {
    if (!username || !videoTag) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.deleteCreatorVideo(username, videoTag);

      if (res.data?.success) {
        setVideoThumbs((current) =>
          Array.isArray(current)
            ? current.filter(
                (item) =>
                  String(item.videoTag ?? item.video_tag) !== String(videoTag),
              )
            : current,
        );

        setVideo((current) =>
          String(current?.videoTag ?? current?.video_tag) === String(videoTag)
            ? {}
            : current,
        );

        setVideoData((current) =>
          String(current?.videoTag ?? current?.video_tag) === String(videoTag)
            ? {}
            : current,
        );
      }

      return res.data;
    } catch (requestError) {
      console.error("deleteCreatorVideo failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to delete video"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getCreatorVideoPlay = async (videoTag) => {
    if (!username || !videoTag) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.getCreatorVideoPlay(username, videoTag);

      setVideo(res.data);

      return res.data;
    } catch (requestError) {
      console.error("getCreatorVideoPlay failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to load video"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const searchCreatorVideos = async ({ searchValue, limit, offset } = {}) => {
    if (!username) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.searchCreatorVideos({
        username,
        searchValue,
        limit,
        offset,
      });

      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.videos)
          ? res.data.videos
          : [];

      setVideoThumbs((current) =>
        Number(offset) > 0
          ? [
              ...current.filter(
                (item) =>
                  !data.some(
                    (incoming) =>
                      String(incoming.videoTag) === String(item.videoTag),
                  ),
              ),
              ...data,
            ]
          : data,
      );

      return data;
    } catch (requestError) {
      console.error("searchCreatorVideos failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to search videos"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const searchCreatorVideoTag = async (videoTag) => {
    if (!videoTag) return false;

    try {
      setLoading(true);
      setError("");

      const res = await service.searchCreatorVideoTag(videoTag);

      setSearchVideoTagResults(res.data);

      return res.data;
    } catch (requestError) {
      console.error("searchCreatorVideoTag failed:", requestError);

      setError(getErrorMessage(requestError, "Failed to search video tag"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetCreatorVideos = () => {
    setVideoThumbs([]);
    setVideo({});
    setVideoData({});
    setSearchVideoTagResults({});
    setLoading(false);
    setError("");
  };

  return {
    videoThumbs,
    video,
    videoData,
    searchVideoTagResults,
    loading,
    error,

    setVideoThumbs,
    setVideo,
    setVideoData,
    setError,

    getCreatorVideoThumbs,
    getCreatorVideoData,
    getCreatorVideoEditData,
    updateCreatorVideo,
    deleteCreatorVideo,
    getCreatorVideoPlay,
    searchCreatorVideos,
    searchCreatorVideoTag,
    resetCreatorVideos,
  };
};

export default useCreatorVideos;
